<template>
  <transition name="voice-modal-fade">
    <div
      v-if="show"
      class="voice-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="voice-modal-heading"
    >
      <div class="voice-modal-backdrop"></div>

      <div class="voice-modal-frame">
        <div class="voice-modal-orb orb-left"></div>
        <div class="voice-modal-orb orb-right"></div>

        <div class="voice-modal-window">
          <header class="voice-modal-header">
            <div class="voice-modal-title">
              <span class="voice-modal-eyebrow">MSDAT Voice Assistant</span>
              <h2 id="voice-modal-heading" class="voice-modal-heading">Voice Control</h2>
              <p class="voice-modal-subcopy">
                Speak naturally to navigate dashboards and sections.
              </p>
            </div>

            <div class="voice-modal-header-actions">
              <div class="voice-status-pill" :class="`theme-${statusTheme}`">
                <span class="status-dot"></span>
                <span>{{ statusLabel }}</span>
              </div>

              <button type="button" class="voice-close-button" @click="$emit('close')">
                <b-icon icon="x-lg"></b-icon>
              </button>
            </div>
          </header>

          <div class="voice-assistant-shell">
            <section class="voice-command-deck">
              <div class="command-deck-copy">
                <span class="hero-pill">Experimental</span>
                <h3>Your voice copilot for MSDAT</h3>
                <p>{{ statusText }}</p>
              </div>

              <div class="voice-stage-panel">
                <div class="voice-stage">
                  <div class="mic-aura" :class="{ active: isListening }"></div>
                  <b-button
                    :variant="micButtonVariant"
                    class="rounded-circle mic-button d-flex align-items-center justify-content-center"
                    :class="{ listening: isListening }"
                    :disabled="!recognitionSupported || status === 'processing'"
                    @click="
                      micPermission !== 'granted' ? requestMicrophoneAccess() : toggleListening()
                    "
                  >
                    <b-icon
                      :icon="isListening ? 'stop-fill' : 'mic-fill'"
                      font-scale="1.6"
                      class="text-white"
                    ></b-icon>
                  </b-button>

                  <div v-if="isListening" class="sound-wave d-flex justify-content-center mt-3">
                    <span class="wave-bar"></span>
                    <span class="wave-bar"></span>
                    <span class="wave-bar"></span>
                    <span class="wave-bar"></span>
                    <span class="wave-bar"></span>
                  </div>
                </div>

                <div class="voice-actions">
                  <p class="action-label">
                    {{ isListening ? 'Tap to stop capture' : listeningHint }}
                  </p>

                  <div class="voice-mini-stats">
                    <div class="mini-stat">
                      <span class="mini-stat-label">Input</span>
                      <strong>Natural voice</strong>
                    </div>
                    <div class="mini-stat">
                      <span class="mini-stat-label">Mode</span>
                      <strong>{{ statusLabel }}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div class="voice-prompt-panel">
                <div class="panel-heading-row">
                  <span class="panel-kicker">Suggested prompts</span>
                  <b-button variant="light" class="test-button" @click="testAPIDirectly">
                    Try sample command
                  </b-button>
                </div>

                <div class="suggested-prompts">
                  <button
                    v-for="prompt in suggestedPrompts"
                    :key="prompt"
                    type="button"
                    class="prompt-chip"
                    :disabled="status === 'processing'"
                    @click="runSuggestedPrompt(prompt)"
                  >
                    {{ prompt }}
                  </button>
                </div>
              </div>
            </section>

            <section class="voice-insight-stack">
              <article v-if="assistantMessage" class="response-card assistant-card">
                <div class="response-label">Assistant</div>
                <p class="assistant-message">
                  {{ assistantMessage }}<span v-if="isTypingIntro" class="typing-cursor">|</span>
                </p>
              </article>

              <section
                v-if="micPermission !== 'granted' || !recognitionSupported"
                class="info-grid"
              >
                <div v-if="micPermission !== 'granted'" class="info-card" :class="permissionTone">
                  <div class="info-card-icon">
                    <b-icon icon="info-circle-fill"></b-icon>
                  </div>
                  <div>
                    <h3>Microphone access</h3>
                    <p>
                      {{
                        micPermission === 'denied'
                          ? 'Microphone access is blocked. Enable it in your browser settings to use voice control.'
                          : 'Microphone permission is required before the assistant can start listening.'
                      }}
                    </p>
                  </div>
                </div>

                <div v-if="!recognitionSupported" class="info-card danger">
                  <div class="info-card-icon">
                    <b-icon icon="exclamation-triangle-fill"></b-icon>
                  </div>
                  <div>
                    <h3>Browser support</h3>
                    <p>
                      Speech recognition works best in recent versions of Chrome, Edge, or Safari.
                    </p>
                  </div>
                </div>
              </section>

              <section
                v-if="transcript || status === 'processing' || audioUrl"
                class="response-grid response-grid-compact"
              >
                <article v-if="transcript" class="response-card glass-card">
                  <div class="response-label">Captured Request</div>
                  <p>{{ transcript }}</p>
                </article>

                <article v-if="status === 'processing'" class="response-card processing-card">
                  <div class="response-label">Assistant is thinking</div>
                  <div class="processing-row">
                    <div class="rolling-spinner"></div>
                    <p>Processing your request and preparing a response.</p>
                  </div>
                  <b-button
                    variant="outline-light"
                    size="sm"
                    class="cancel-button"
                    @click="cancelRequest"
                  >
                    Cancel request
                  </b-button>
                </article>

                <article v-show="audioUrl" class="audio-card">
                  <div class="response-label">Audio playback</div>
                  <audio
                    ref="audioRef"
                    controls
                    class="w-100"
                    @error="handleAudioError"
                    @play="handleAudioPlay"
                    @pause="handleAudioPause"
                    @ended="handleAudioEnded"
                  >
                    Your browser does not support the audio element.
                  </audio>
                </article>
              </section>

              <section class="debug-card">
                <details>
                  <summary>Debug Information</summary>
                  <div class="debug-log">
                    <div v-if="debugInfo.length === 0" class="debug-empty">
                      No debug information available
                    </div>
                    <div v-else>
                      <div v-for="(info, index) in debugInfo" :key="index" class="debug-item">
                        {{ info }}
                      </div>
                    </div>
                  </div>
                </details>
              </section>
            </section>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import store from '@/store';
import {
  ref, computed, onMounted, onBeforeUnmount, watch,
} from 'vue';

export default {
  name: 'VoiceControlModal',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const sanitizeJsonResponse = (text) => {
      try {
        return JSON.parse(text);
      } catch {
        const cleanText = text.replace(/"\s*"([^"]+)"\s*"/, '"$1"');
        return JSON.parse(cleanText);
      }
    };

    const isListening = ref(false);
    const transcript = ref('');
    const interimTranscript = ref(''); // For showing real-time transcription
    const finalTranscript = ref(''); // Accumulated final results
    const manualStop = ref(false); // Track if user manually stopped
    const textResponse = ref(''); // Added for text response
    const introMessage
      = "Hello 👋\nI'm your voice assistant, here to help you navigate dashboards on MSDAT.\nJust tell me what you need.";
    const typedIntroMessage = ref('');
    const isTypingIntro = ref(false);
    const introTypingTimeout = ref(null);
    const status = ref('idle'); // idle, listening, processing, response
    const audioUrl = ref(null);
    const audioRef = ref(null);
    const recognitionRef = ref(null);
    const debugInfo = ref([]);
    const micPermission = ref('prompt'); // granted, denied, prompt
    const recognitionSupported = ref(true);
    const abortController = ref(null); // For cancelling API requests
    const listeningTimeout = ref(null); // For inactivity auto-stop timeout
    const stopReason = ref(null);
    const recoveryPending = ref(false);
    const pendingNavigationWindow = ref(null);
    let sendCommandToAPI;
    let startListening;
    let stopListening;
    const suggestedPrompts = [
      'Take me to demographics dashboard and Zonal Analysis Section',
      'Take me to Health Service Access Dashboard ',
      'Take me to Health Facility Dashboard',
    ];
    const MAX_LISTENING_DURATION = 30000;

    const clearIntroTypingTimeout = () => {
      if (introTypingTimeout.value) {
        clearTimeout(introTypingTimeout.value);
        introTypingTimeout.value = null;
      }
    };

    const startIntroTyping = () => {
      clearIntroTypingTimeout();
      typedIntroMessage.value = '';
      isTypingIntro.value = true;
      let currentIndex = 0;

      const typeNextCharacter = () => {
        if (!props.show) {
          clearIntroTypingTimeout();
          isTypingIntro.value = false;
          return;
        }

        if (currentIndex >= introMessage.length) {
          isTypingIntro.value = false;
          introTypingTimeout.value = null;
          return;
        }

        typedIntroMessage.value += introMessage[currentIndex];
        currentIndex += 1;
        introTypingTimeout.value = setTimeout(typeNextCharacter, 28);
      };

      typeNextCharacter();
    };

    // Add this function to log debug information
    const addDebugInfo = (message) => {
      // console.log(message);
      debugInfo.value.push(`${new Date().toLocaleTimeString()}: ${message}`);
    };

    const handleAudioError = (event) => {
      addDebugInfo(`Audio error: ${event.target.error?.message || 'Unknown error'}`);
    };

    const handleAudioPlay = () => {
      addDebugInfo('Audio started playing');
    };

    const handleAudioPause = () => {
      addDebugInfo('Audio paused');
    };

    const handleAudioEnded = () => {
      addDebugInfo('Audio playback completed');
    };

    // Process audio responses
    const processAudioResponse = (audioResponse) => {
      addDebugInfo(`Audio response URL received: ${audioResponse}`);
      audioUrl.value = audioResponse;
      status.value = 'response';
      // Play the audio
      if (audioRef.value) {
        addDebugInfo('Setting audio source and attempting to play...');
        audioRef.value.src = audioResponse;
        audioRef.value.load();
        audioRef.value.play().catch((error) => {
          addDebugInfo(`Error playing audio: ${error.message}`);
        });
        addDebugInfo('Audio playback initiated');
      } else {
        addDebugInfo('Audio element reference is null');
      }
    };

    // Cancel ongoing API request
    const cancelRequest = () => {
      if (abortController.value) {
        abortController.value.abort();
        abortController.value = null;
      }
      status.value = 'idle';
      addDebugInfo('Request cancelled by user');
    };

    const clearListeningTimeout = () => {
      if (listeningTimeout.value) {
        clearTimeout(listeningTimeout.value);
        listeningTimeout.value = null;
      }
    };

    const resetVoiceSession = () => {
      clearListeningTimeout();
      status.value = 'idle';
      isListening.value = false;
      stopReason.value = null;
      manualStop.value = false;
      recoveryPending.value = false;
    };

    const clearVoiceOutputs = () => {
      transcript.value = '';
      interimTranscript.value = '';
      finalTranscript.value = '';
      textResponse.value = '';
      audioUrl.value = null;
      typedIntroMessage.value = '';
      isTypingIntro.value = false;
    };

    const assistantMessage = computed(() => textResponse.value || typedIntroMessage.value);

    const normalizeText = (value) => value
      .toLowerCase()
      .replace(/[^\w\s-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const normalizeResponseType = (value) => (typeof value === 'string' ? normalizeText(value).replace(/\s+/g, '') : '');

    const getSectionTargetFromResponse = (data) => {
      if (!data || typeof data !== 'object') {
        return '';
      }

      const responseType = normalizeResponseType(data.type);
      const sectionTypes = ['section', 'internalsection', 'internalnavigation'];

      if (!sectionTypes.includes(responseType)) {
        return '';
      }

      return data.section || data.section_title || data.title || data.name || data.route || '';
    };

    const getNavigationTargetFromResponse = (data) => {
      if (!data || typeof data !== 'object') {
        return '';
      }

      if (normalizeResponseType(data.type) !== 'navigation') {
        return '';
      }

      return data.url || data.route || '';
    };

    const closePendingNavigationWindow = () => {
      if (pendingNavigationWindow.value && !pendingNavigationWindow.value.closed) {
        pendingNavigationWindow.value.close();
      }

      pendingNavigationWindow.value = null;
    };

    const teardownVoiceSession = () => {
      addDebugInfo('Resetting voice session after modal close');
      clearIntroTypingTimeout();
      clearVoiceOutputs();
      closePendingNavigationWindow();

      if (abortController.value) {
        abortController.value.abort();
        abortController.value = null;
      }

      if (recognitionRef.value) {
        try {
          recognitionRef.value.abort();
        } catch (error) {
          addDebugInfo(`Error aborting recognition during reset: ${error}`);
        }
      }

      if (audioRef.value) {
        audioRef.value.pause();
        audioRef.value.currentTime = 0;
      }

      resetVoiceSession();
    };

    const ensurePendingNavigationWindow = () => {
      if (pendingNavigationWindow.value && !pendingNavigationWindow.value.closed) {
        return pendingNavigationWindow.value;
      }

      const openedWindow = window.open('', '_blank');

      if (!openedWindow) {
        addDebugInfo('Unable to pre-open navigation tab - browser blocked it');
        pendingNavigationWindow.value = null;
        return null;
      }

      try {
        openedWindow.opener = null;
        openedWindow.document.title = 'MSDAT Assistant';
        openedWindow.document.documentElement.innerHTML = `
          <head>
            <title>MSDAT Assistant</title>
            <meta charset="utf-8">
            <meta name="viewport" content="width=device-width, initial-scale=1">
            <style>
              :root {
                color-scheme: light;
                --bg-1: #f4fbf8;
                --bg-2: #e5f5ee;
                --panel: rgba(255, 255, 255, 0.78);
                --panel-border: rgba(0, 125, 83, 0.14);
                --text-main: #123629;
                --text-soft: #547468;
                --brand: #007d53;
                --brand-soft: #2ab27b;
                --glow: rgba(0, 125, 83, 0.18);
              }

              * {
                box-sizing: border-box;
              }

              body {
                margin: 0;
                min-height: 100vh;
                display: grid;
                place-items: center;
                overflow: hidden;
                font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
                background:
                  radial-gradient(circle at top left, rgba(42, 178, 123, 0.2), transparent 34%),
                  radial-gradient(circle at bottom right, rgba(0, 125, 83, 0.18), transparent 30%),
                  linear-gradient(145deg, var(--bg-1), var(--bg-2));
                color: var(--text-main);
              }

              .orb {
                position: fixed;
                border-radius: 999px;
                filter: blur(10px);
                opacity: 0.6;
                animation: drift 9s ease-in-out infinite;
              }

              .orb-one {
                width: 220px;
                height: 220px;
                top: -60px;
                left: -40px;
                background: rgba(42, 178, 123, 0.22);
              }

              .orb-two {
                width: 260px;
                height: 260px;
                right: -90px;
                bottom: -80px;
                background: rgba(0, 125, 83, 0.16);
                animation-delay: -3s;
              }

              .shell {
                position: relative;
                width: min(92vw, 560px);
                padding: 40px 34px;
                border-radius: 28px;
                background: var(--panel);
                border: 1px solid var(--panel-border);
                box-shadow:
                  0 28px 60px rgba(18, 54, 41, 0.12),
                  inset 0 1px 0 rgba(255, 255, 255, 0.7);
                backdrop-filter: blur(12px);
              }

              .eyebrow {
                display: inline-flex;
                align-items: center;
                gap: 8px;
                padding: 7px 12px;
                border-radius: 999px;
                background: rgba(0, 125, 83, 0.08);
                color: var(--brand);
                font-size: 12px;
                font-weight: 700;
                letter-spacing: 0.08em;
                text-transform: uppercase;
              }

              .eyebrow::before {
                content: "";
                width: 8px;
                height: 8px;
                border-radius: 999px;
                background: linear-gradient(135deg, var(--brand), var(--brand-soft));
                box-shadow: 0 0 0 6px rgba(0, 125, 83, 0.08);
              }

              h1 {
                margin: 18px 0 10px;
                font-size: clamp(30px, 5vw, 42px);
                line-height: 1.05;
                letter-spacing: -0.03em;
              }

              p {
                margin: 0;
                color: var(--text-soft);
                font-size: 15px;
                line-height: 1.65;
              }

              .loader-row {
                margin-top: 28px;
                display: flex;
                align-items: center;
                gap: 18px;
              }

              .loader-ring {
                position: relative;
                width: 74px;
                height: 74px;
                border-radius: 50%;
                background: conic-gradient(from 180deg, rgba(0, 125, 83, 0.12), var(--brand), rgba(42, 178, 123, 0.18));
                animation: spin 1.2s linear infinite;
                box-shadow: 0 14px 30px var(--glow);
              }

              .loader-ring::before {
                content: "";
                position: absolute;
                inset: 7px;
                border-radius: 50%;
                background: linear-gradient(145deg, rgba(255, 255, 255, 0.95), rgba(236, 248, 242, 0.98));
              }

              .loader-ring::after {
                content: "";
                position: absolute;
                inset: 24px;
                border-radius: 50%;
                background: linear-gradient(135deg, var(--brand), var(--brand-soft));
                box-shadow: 0 0 24px rgba(0, 125, 83, 0.24);
              }

              .loader-copy {
                flex: 1;
              }

              .loader-title {
                margin: 0 0 6px;
                font-size: 18px;
                font-weight: 700;
                color: var(--text-main);
              }

              .progress {
                margin-top: 18px;
                height: 10px;
                border-radius: 999px;
                background: rgba(0, 125, 83, 0.08);
                overflow: hidden;
              }

              .progress-bar {
                width: 42%;
                height: 100%;
                border-radius: inherit;
                background: linear-gradient(90deg, var(--brand), var(--brand-soft));
                box-shadow: 0 6px 16px rgba(0, 125, 83, 0.22);
                animation: sweep 1.8s ease-in-out infinite;
              }

              .caption {
                margin-top: 16px;
                font-size: 13px;
                color: rgba(84, 116, 104, 0.9);
              }

              @keyframes spin {
                to {
                  transform: rotate(360deg);
                }
              }

              @keyframes sweep {
                0% {
                  transform: translateX(-18%);
                  width: 28%;
                }
                50% {
                  transform: translateX(120%);
                  width: 52%;
                }
                100% {
                  transform: translateX(235%);
                  width: 28%;
                }
              }

              @keyframes drift {
                0%, 100% {
                  transform: translateY(0) translateX(0);
                }
                50% {
                  transform: translateY(18px) translateX(12px);
                }
              }
            </style>
          </head>
          <body>
            <div class="orb orb-one"></div>
            <div class="orb orb-two"></div>
            <main class="shell">
              <div class="eyebrow">MSDAT Voice Assistant</div>
              <h1>Preparing your dashboard</h1>
              <p>Your navigation request is being processed. This tab will open the destination as soon as it is ready.</p>
              <div class="loader-row">
                <div class="loader-ring" aria-hidden="true"></div>
                <div class="loader-copy">
                  <p class="loader-title">Loading your destination</p>
                  <p>Connecting your voice command to the requested dashboard view and section.</p>
                </div>
              </div>
              <div class="progress" aria-hidden="true">
                <div class="progress-bar"></div>
              </div>
              <p class="caption">Please keep this tab open for a moment.</p>
            </main>
          </body>
        `;
      } catch (error) {
        addDebugInfo(`Opened placeholder tab but could not write to it: ${error}`);
      }

      pendingNavigationWindow.value = openedWindow;
      addDebugInfo('Navigation tab pre-opened and waiting for response');
      return openedWindow;
    };

    const navigateToTarget = async (targetUrl) => {
      const destinationWindow = ensurePendingNavigationWindow();

      if (destinationWindow && !destinationWindow.closed) {
        destinationWindow.location.replace(targetUrl.href);
        destinationWindow.focus();
        pendingNavigationWindow.value = null;
        addDebugInfo('Navigation target loaded in pre-opened tab');
        return;
      }

      addDebugInfo('Pre-opened tab unavailable - attempting direct new-tab navigation');
      const openedWindow = window.open(targetUrl.href, '_blank', 'noopener,noreferrer');

      if (openedWindow) {
        addDebugInfo('Navigation target opened in a new tab');
        return;
      }

      addDebugInfo('Popup blocked by browser - falling back to same-tab navigation');
      window.location.assign(targetUrl.href);
    };

    function finalizeTranscriptProcessing() {
      const fullTranscript = finalTranscript.value.trim();

      if (fullTranscript) {
        addDebugInfo(`Processing final transcript: "${fullTranscript}"`);
        transcript.value = fullTranscript;
        status.value = 'processing';
        sendCommandToAPI(fullTranscript);
        emit('command', fullTranscript);
      } else {
        addDebugInfo('No transcript to process');
        closePendingNavigationWindow();
        status.value = 'idle';
      }
    }

    function scheduleListeningTimeout() {
      clearListeningTimeout();

      listeningTimeout.value = setTimeout(() => {
        if (isListening.value || status.value === 'listening') {
          addDebugInfo('Auto-stopping after 30 seconds of inactivity');
          stopListening('timeout');
        }
      }, MAX_LISTENING_DURATION);
    }

    const restartRecognition = (reason = 'unexpected end') => {
      if (
        !recognitionRef.value
        || !recognitionSupported.value
        || manualStop.value
        || stopReason.value === 'timeout'
        || status.value !== 'listening'
      ) {
        return;
      }

      addDebugInfo(`Attempting recognition recovery after ${reason}`);
      recoveryPending.value = true;

      setTimeout(() => {
        try {
          if (
            recognitionRef.value
            && recognitionSupported.value
            && !manualStop.value
            && stopReason.value !== 'timeout'
            && status.value === 'listening'
          ) {
            recognitionRef.value.start();
            scheduleListeningTimeout();
            addDebugInfo('Recognition restarted');
          }
        } catch (error) {
          addDebugInfo(`Error restarting recognition: ${error}`);
          if (status.value === 'listening') {
            restartRecognition('restart failure');
          } else {
            isListening.value = false;
            recoveryPending.value = false;
          }
          return;
        }

        recoveryPending.value = false;
      }, 250);
    };

    // Send command to API
    sendCommandToAPI = async (command) => {
      addDebugInfo(`Preparing to send command to API: "${command}"`);
      const payload = JSON.stringify({ command });
      addDebugInfo(`API request payload: ${payload}`);
      clearIntroTypingTimeout();
      isTypingIntro.value = false;
      textResponse.value = ''; // Clear previous text response

      // Create new AbortController for this request
      abortController.value = new AbortController();

      try {
        addDebugInfo('Sending request to API endpoint...');

        const response = await fetch(
          'https://n8n.e4eweb.space/webhook/9ddd74ac-f821-45c9-a341-357971c5e359',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: payload,
            signal: abortController.value.signal,
          },
        );

        const responseText = await response.text();

        addDebugInfo(`API response status: ${response.status}`);
        addDebugInfo('--- BEGIN RAW API RESPONSE TEXT ---');
        addDebugInfo(responseText);
        addDebugInfo('--- END RAW API RESPONSE TEXT ---');

        if (!response.ok) {
          throw new Error(
            `API request failed with status ${response.status}. Response: ${responseText}`,
          );
        }

        // ✅ Try to parse JSON
        let data;
        try {
          data = JSON.parse(responseText);
        } catch (err) {
          addDebugInfo(`Error parsing JSON: ${err.message}`);
          try {
            data = sanitizeJsonResponse(responseText);
            addDebugInfo('Recovered API response with fallback JSON sanitization');
          } catch {
            throw new Error(`Failed to parse response: ${err.message}`);
          }
        }

        console.log('Voice control API response:', data);
        addDebugInfo('Parsed API response logged to console for inspection');

        // ✅ Handle navigation type
        const navigationTarget = getNavigationTargetFromResponse(data);
        if (navigationTarget) {
          addDebugInfo(`Navigation detected — redirecting to ${navigationTarget}`);

          const rawRoute = navigationTarget.trim();
          const targetSection = typeof data.section === 'string' ? data.section.trim() : '';
          let targetUrl;

          try {
            // Try to construct URL directly (handles full URLs correctly)
            targetUrl = new URL(rawRoute);

            // If it's already a valid absolute URL, use it directly
          } catch {
            // If it fails, it's a relative path — build from origin
            const cleanPath = rawRoute.replace(/^\/+/, '');
            targetUrl = new URL(cleanPath, window.location.origin);
          }

          if (targetSection) {
            targetUrl.searchParams.set('section', targetSection);
            addDebugInfo(`Navigation section queued for destination dashboard: ${targetSection}`);
          }

          addDebugInfo(`Final navigation URL: ${targetUrl.href}`);
          await navigateToTarget(targetUrl);
          resetVoiceSession();
          emit('close');
          return;
        }

        const targetSection = getSectionTargetFromResponse(data);
        if (targetSection) {
          closePendingNavigationWindow();
          addDebugInfo(`Internal section switch detected — moving to ${targetSection}`);

          store.commit('MSDAT_STORE/SET_SECTION', targetSection);
          resetVoiceSession();
          emit('close');
          return;
        }
        // ✅ Continue normal response flow
        addDebugInfo(`API response data: ${JSON.stringify(data)}`);

        if (data.text_response) {
          closePendingNavigationWindow();
          textResponse.value = data.text_response;
          addDebugInfo(`Text response received: ${data.text_response}`);
        }

        if (data.audio_response) {
          closePendingNavigationWindow();
          processAudioResponse(data.audio_response);
        } else {
          addDebugInfo('No audio response in API response');
          if (!data.text_response) {
            closePendingNavigationWindow();
            status.value = 'idle';
          }
        }
      } catch (error) {
        closePendingNavigationWindow();
        if (error.name === 'AbortError') {
          addDebugInfo('Request was aborted');
          return;
        }
        const errorMessage = error instanceof Error ? error.message : String(error);
        // console.error('Error in sendCommandToAPI:', errorMessage);
        addDebugInfo(`API call failed: ${errorMessage}`);
        status.value = 'idle';
      } finally {
        abortController.value = null;
      }
    };

    // Initialize speech recognition
    const initializeSpeechRecognition = () => {
      addDebugInfo('Initializing speech recognition...');
      if (typeof window !== 'undefined') {
        // Check if SpeechRecognition is available
        const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognitionAPI) {
          addDebugInfo('Speech recognition API is available');
          recognitionRef.value = new SpeechRecognitionAPI();
          // Configure the recognition for better accuracy
          recognitionRef.value.continuous = true; // Keep listening until manually stopped
          recognitionRef.value.interimResults = true; // Show results as user speaks
          recognitionRef.value.maxAlternatives = 3; // Get multiple alternatives for better accuracy
          recognitionRef.value.lang = 'en-US';

          // Set up event handlers
          recognitionRef.value.onstart = () => {
            addDebugInfo('Speech recognition started');
            isListening.value = true;
            recoveryPending.value = false;
            scheduleListeningTimeout();
          };

          recognitionRef.value.onspeechstart = () => {
            addDebugInfo('Speech detected');
            scheduleListeningTimeout();
          };

          recognitionRef.value.onsoundstart = () => {
            addDebugInfo('Sound detected');
            scheduleListeningTimeout();
          };

          recognitionRef.value.onresult = (event) => {
            let interim = '';
            let final = finalTranscript.value;

            // Process all results
            for (let i = event.resultIndex; i < event.results.length; i++) {
              const result = event.results[i];
              const transcriptText = result[0].transcript;
              const confidence = result[0].confidence;

              if (result.isFinal) {
                // Log alternatives for debugging
                if (result.length > 1) {
                  const alternatives = Array.from(result)
                    .map(
                      (alt, idx) => `Alt ${idx + 1}: "${alt.transcript}" (${(alt.confidence * 100).toFixed(
                        1,
                      )}%)`,
                    )
                    .join(', ');
                  addDebugInfo(`Alternatives: ${alternatives}`);
                }

                final += `${transcriptText} `;
                addDebugInfo(
                  `Final result: "${transcriptText}" (confidence: ${(confidence * 100).toFixed(
                    1,
                  )}%)`,
                );
              } else {
                interim += transcriptText;
              }
            }

            finalTranscript.value = final;
            interimTranscript.value = interim;

            // Update display transcript (final + interim)
            transcript.value = (final + interim).trim();
            scheduleListeningTimeout();
            addDebugInfo(`Current transcript: "${transcript.value}"`);
          };

          recognitionRef.value.onspeechend = () => {
            addDebugInfo('Speech ended (silence detected)');
          };

          recognitionRef.value.onerror = (event) => {
            addDebugInfo(`Speech recognition error: ${event.error}`);

            // Handle specific errors
            if (event.error === 'no-speech') {
              addDebugInfo('No speech detected - continuing to listen...');
              restartRecognition('no-speech');
              return;
            }

            if (event.error === 'aborted') {
              addDebugInfo('Recognition aborted');
              if (
                !manualStop.value
                && stopReason.value !== 'timeout'
                && status.value === 'listening'
              ) {
                restartRecognition('aborted');
              }
              return;
            }

            if (event.error === 'network') {
              addDebugInfo('Network error - check your connection');
              restartRecognition('network error');
              return;
            }

            if (event.error === 'audio-capture') {
              addDebugInfo('Audio capture failed - microphone may be unavailable');
              restartRecognition('audio-capture');
              return;
            }

            if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
              addDebugInfo('Microphone or speech recognition permission not allowed');
              micPermission.value = 'denied';
              isListening.value = false;
              status.value = 'idle';
              return;
            }

            addDebugInfo('Recoverable speech recognition error encountered');
            restartRecognition(event.error);
          };

          recognitionRef.value.onend = () => {
            addDebugInfo(
              `Speech recognition ended. Manual stop: ${manualStop.value}, isListening: ${isListening.value}`,
            );

            // If user manually stopped, process the transcript
            if (manualStop.value || stopReason.value === 'timeout') {
              manualStop.value = false;
              isListening.value = false;
              stopReason.value = null;
              finalizeTranscriptProcessing();
            } else if (isListening.value && status.value === 'listening') {
              // If recognition ended but user didn't stop, restart it
              addDebugInfo('Recognition ended unexpectedly, restarting...');
              restartRecognition('unexpected end');
            } else {
              isListening.value = false;
              stopReason.value = null;
              recoveryPending.value = false;
              if (status.value === 'listening') {
                status.value = 'idle';
              }
            }
          };

          recognitionSupported.value = true;
        } else {
          addDebugInfo('Speech recognition is NOT supported in this browser');
          recognitionSupported.value = false;
        }
      }
    };

    // Check for microphone permissions
    const checkMicrophonePermission = async () => {
      try {
        addDebugInfo('Checking microphone permission...');
        const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
        micPermission.value = permissionStatus.state;
        addDebugInfo(`Microphone permission status: ${permissionStatus.state}`);
        permissionStatus.onchange = () => {
          micPermission.value = permissionStatus.state;
          addDebugInfo(`Microphone permission changed to: ${permissionStatus.state}`);
        };
      } catch (error) {
        addDebugInfo(`Error checking microphone permission: ${error}`);
      }
    };

    // Request microphone access explicitly
    const requestMicrophoneAccess = async () => {
      try {
        addDebugInfo('Requesting microphone access...');
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        micPermission.value = 'granted';
        addDebugInfo('Microphone access granted');
        // Clean up the stream immediately since we just needed it for permissions
        stream.getTracks().forEach((track) => track.stop());
        // Initialize speech recognition after permission is granted
        initializeSpeechRecognition();
      } catch (error) {
        micPermission.value = 'denied';
        addDebugInfo(`Microphone access denied: ${error}`);
      }
    };

    // Toggle listening on/off
    const toggleListening = () => {
      if (isListening.value) {
        stopListening();
      } else {
        startListening();
      }
    };

    // Start listening function
    startListening = () => {
      addDebugInfo('Starting to listen...');
      clearIntroTypingTimeout();
      isTypingIntro.value = false;
      // Clear previous results
      transcript.value = '';
      finalTranscript.value = '';
      interimTranscript.value = '';
      manualStop.value = false;
      stopReason.value = null;
      textResponse.value = ''; // Clear previous text response
      audioUrl.value = null;
      status.value = 'listening';
      // Check if we have microphone permission
      if (micPermission.value !== 'granted') {
        addDebugInfo('No microphone permission, requesting access...');
        requestMicrophoneAccess();
        return;
      }
      // Check if speech recognition is supported and initialized
      if (!recognitionSupported.value) {
        addDebugInfo('Speech recognition not supported in this browser');
        return;
      }
      if (!recognitionRef.value) {
        addDebugInfo('Speech recognition not initialized, initializing now...');
        initializeSpeechRecognition();
      }
      // Start recognition
      try {
        addDebugInfo('Attempting to start speech recognition');
        recognitionRef.value.start();
        scheduleListeningTimeout();
      } catch (error) {
        addDebugInfo(`Error starting speech recognition: ${error}`);
        // If already started, stop and restart
        if (error instanceof DOMException && error.name === 'InvalidStateError') {
          addDebugInfo('Recognition was already running, stopping and restarting');
          try {
            recognitionRef.value.stop();
            setTimeout(() => {
              recognitionRef.value.start();
              scheduleListeningTimeout();
            }, 100);
          } catch (stopError) {
            addDebugInfo(`Error stopping recognition: ${stopError}`);
          }
        }
      }
    };

    // Stop listening function
    stopListening = (reason = 'manual') => {
      addDebugInfo(`Stopping listening (${reason} stop)...`);

      stopReason.value = reason;
      manualStop.value = reason === 'manual';
      recoveryPending.value = false;

      if (reason === 'manual') {
        ensurePendingNavigationWindow();
      }

      clearListeningTimeout();

      if (recognitionRef.value) {
        try {
          recognitionRef.value.stop();
          addDebugInfo('Speech recognition stop requested');
        } catch (error) {
          addDebugInfo(`Error stopping speech recognition: ${error}`);
          // If stop fails, still process what we have
          manualStop.value = false;
          isListening.value = false;
          stopReason.value = null;
          finalizeTranscriptProcessing();
        }
      } else {
        addDebugInfo('Speech recognition not initialized');
        manualStop.value = false;
        isListening.value = false;
        stopReason.value = null;
        status.value = 'idle';
      }
    };

    // Test API directly
    const testAPIDirectly = () => {
      const testCommand = 'Navigate me to health outcome dashboard';
      addDebugInfo(`Testing API with command: "${testCommand}"`);
      ensurePendingNavigationWindow();
      sendCommandToAPI(testCommand);
    };

    const runSuggestedPrompt = (prompt) => {
      clearIntroTypingTimeout();
      isTypingIntro.value = false;
      transcript.value = prompt;
      finalTranscript.value = prompt;
      interimTranscript.value = '';
      addDebugInfo(`Running suggested prompt: "${prompt}"`);
      ensurePendingNavigationWindow();
      status.value = 'processing';
      sendCommandToAPI(prompt);
      emit('command', prompt);
    };

    // Computed properties
    const micButtonVariant = computed(() => {
      if (isListening.value) return 'danger'; // Red when listening
      if (micPermission.value !== 'granted') return 'warning'; // Yellow if permission needed
      return 'primary'; // Green if granted and idle
    });

    const statusText = computed(() => {
      if (isListening.value) {
        return 'Listening... Speak now';
      }
      if (status.value === 'processing') {
        return 'Processing your request...';
      }
      if (status.value === 'response') {
        return 'Playing response...';
      }
      if (micPermission.value !== 'granted') {
        return 'Click the microphone to grant access';
      }
      return 'Click the microphone to start speaking';
    });

    const statusLabel = computed(() => {
      if (isListening.value) return 'Live Listening';
      if (status.value === 'processing') return 'Processing';
      if (status.value === 'response') return 'Response Ready';
      if (micPermission.value !== 'granted') return 'Permission Needed';
      return 'Ready';
    });

    const statusTheme = computed(() => {
      if (isListening.value) return 'listening';
      if (status.value === 'processing') return 'processing';
      if (status.value === 'response') return 'response';
      if (micPermission.value !== 'granted') return 'warning';
      return 'idle';
    });

    const listeningHint = computed(() => {
      if (micPermission.value !== 'granted') return 'Enable microphone access';
      return 'Start speaking';
    });

    const permissionTone = computed(() => (micPermission.value === 'denied' ? 'danger' : 'warning'));

    const setBodyScrollLock = (locked) => {
      if (typeof document === 'undefined') {
        return;
      }

      document.body.style.overflow = locked ? 'hidden' : '';
    };

    watch(
      () => props.show,
      (isVisible) => {
        setBodyScrollLock(isVisible);

        if (isVisible) {
          startIntroTyping();
          return;
        }

        teardownVoiceSession();
      },
      { immediate: true },
    );

    // Lifecycle hooks
    onMounted(() => {
      addDebugInfo('Voice assistant component mounted');
      checkMicrophonePermission();
      // Check if speech recognition is supported
      if (typeof window !== 'undefined') {
        const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognitionAPI) {
          addDebugInfo('Speech recognition is NOT supported in this browser');
          recognitionSupported.value = false;
        } else {
          addDebugInfo('Speech recognition API is available');
          // Only initialize if we already have microphone permission
          if (micPermission.value === 'granted') {
            initializeSpeechRecognition();
          }
        }
      }
    });

    onBeforeUnmount(() => {
      addDebugInfo('Cleaning up voice assistant...');
      teardownVoiceSession();
      setBodyScrollLock(false);
    });

    return {
      isListening,
      transcript,
      textResponse,
      assistantMessage,
      isTypingIntro,
      status,
      audioUrl,
      audioRef,
      debugInfo,
      micPermission,
      recognitionSupported,
      micButtonVariant,
      statusText,
      statusLabel,
      statusTheme,
      listeningHint,
      permissionTone,
      suggestedPrompts,
      startListening,
      stopListening,
      toggleListening,
      cancelRequest,
      requestMicrophoneAccess,
      testAPIDirectly,
      runSuggestedPrompt,
      addDebugInfo,
      handleAudioError,
      handleAudioPlay,
      handleAudioPause,
      handleAudioEnded,
    };
  },
};
</script>

<style scoped>
.voice-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: grid;
  place-items: center;
  padding: 24px;
}

.voice-modal-backdrop {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at top left, rgba(8, 122, 98, 0.16), transparent 26%),
    radial-gradient(circle at bottom right, rgba(211, 167, 83, 0.18), transparent 28%),
    rgba(6, 20, 22, 0.62);
  backdrop-filter: blur(16px);
}

.voice-modal-frame {
  position: relative;
  width: min(980px, 100%);
}

.voice-modal-orb {
  position: absolute;
  border-radius: 999px;
  filter: blur(12px);
  opacity: 0.55;
  pointer-events: none;
}

.orb-left {
  top: -28px;
  left: 4%;
  width: 180px;
  height: 180px;
  background: rgba(42, 178, 123, 0.22);
}

.orb-right {
  right: 2%;
  bottom: -20px;
  width: 220px;
  height: 220px;
  background: rgba(212, 166, 75, 0.18);
}

.voice-modal-window {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 32px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.94), rgba(243, 249, 247, 0.9)), #ffffff;
  box-shadow: 0 32px 80px rgba(6, 24, 25, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.76);
  backdrop-filter: blur(18px);
}

.voice-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.25rem;
  padding: 1.35rem 1.5rem 1rem;
  border-bottom: 1px solid rgba(14, 56, 49, 0.08);
  background: linear-gradient(
    135deg,
    rgba(8, 61, 54, 0.96),
    rgba(18, 113, 95, 0.88) 62%,
    rgba(212, 166, 75, 0.82) 140%
  );
  color: #fffdf8;
}

.voice-modal-title {
  display: flex;
  flex-direction: column;
  gap: 0.28rem;
  max-width: 640px;
}

.voice-modal-eyebrow {
  color: rgba(255, 251, 240, 0.82);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.voice-modal-heading {
  margin: 0;
  font-size: clamp(1.5rem, 2vw, 1.95rem);
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.03em;
}

.voice-modal-subcopy {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.45;
  color: rgba(255, 252, 246, 0.82);
}

.voice-modal-header-actions {
  display: flex;
  align-items: center;
  gap: 0.8rem;
}

.voice-status-pill,
.hero-pill,
.response-label,
.panel-kicker {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.voice-status-pill {
  padding: 0.52rem 0.82rem;
  color: #ffffff;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.16);
}

.status-dot {
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 50%;
  background: currentColor;
  box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.12);
}

.theme-warning {
  color: #ffe08a;
}

.theme-listening {
  color: #ffc1c8;
}

.theme-processing {
  color: #d6f5ee;
}

.theme-response {
  color: #f8ddb1;
}

.theme-idle {
  color: #ffffff;
}

.voice-close-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
  cursor: pointer;
  transition: transform 0.2s ease, background-color 0.2s ease;
}

.voice-close-button:hover {
  transform: translateY(-1px);
  background: rgba(255, 255, 255, 0.16);
}

.voice-assistant-shell {
  display: grid;
  grid-template-columns: minmax(280px, 320px) minmax(0, 1fr);
  gap: 1rem;
  padding: 1rem;
  color: #17322c;
}

.voice-command-deck,
.response-card,
.audio-card,
.debug-card,
.info-card {
  border-radius: 26px;
}

.voice-command-deck {
  display: grid;
  gap: 0.9rem;
  align-content: start;
}

.command-deck-copy,
.voice-stage-panel,
.voice-prompt-panel,
.response-card,
.audio-card,
.debug-card {
  padding: 1rem;
  border: 1px solid rgba(17, 42, 36, 0.08);
  background: #ffffff;
}

.command-deck-copy {
  background: radial-gradient(circle at top right, rgba(212, 166, 75, 0.18), transparent 32%),
    linear-gradient(145deg, #0e3831 0%, #14584a 58%, #1d7563 100%);
  color: #fffdf8;
  box-shadow: 0 22px 42px rgba(13, 77, 68, 0.18);
}

.hero-pill {
  padding: 0.36rem 0.72rem;
  margin-bottom: 0.7rem;
  color: #f7ecd1;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.14);
}

.command-deck-copy h3 {
  margin: 0 0 0.45rem;
  font-size: 1.45rem;
  line-height: 1.05;
  letter-spacing: -0.03em;
}

.command-deck-copy p {
  margin: 0;
  color: rgba(255, 252, 244, 0.82);
  line-height: 1.5;
}

.voice-stage-panel {
  background: linear-gradient(180deg, #fbfdfc, #f0f6f3);
  box-shadow: 0 18px 38px rgba(10, 42, 35, 0.08);
}

.voice-stage {
  position: relative;
  min-height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.mic-aura {
  position: absolute;
  width: 160px;
  height: 160px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(18, 113, 95, 0.18), rgba(18, 113, 95, 0.02) 70%);
  transform: scale(0.92);
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.mic-aura.active {
  transform: scale(1.06);
  opacity: 1;
}

.mic-button {
  position: relative;
  z-index: 1;
  width: 88px;
  height: 88px;
  border: 0;
  transition: transform 0.3s ease, box-shadow 0.3s ease, filter 0.3s ease;
  box-shadow: 0 18px 38px rgba(18, 113, 95, 0.28);
}

.mic-button:hover:not(:disabled) {
  transform: translateY(-2px) scale(1.03);
}

.mic-button:active:not(:disabled) {
  transform: scale(0.97);
}

.mic-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}

.mic-button.listening {
  animation: pulse 1.5s ease-in-out infinite;
}

.voice-actions {
  display: grid;
  gap: 0.85rem;
  margin-top: 0.15rem;
}

.action-label {
  margin: 0;
  font-size: 0.94rem;
  font-weight: 600;
  color: #17322c;
  text-align: center;
}

.voice-mini-stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.mini-stat {
  padding: 0.85rem 0.9rem;
  border-radius: 18px;
  background: rgba(242, 247, 245, 0.96);
  border: 1px solid rgba(17, 42, 36, 0.06);
}

.mini-stat-label {
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #69817b;
}

.mini-stat strong {
  font-size: 0.95rem;
  color: #14362f;
}

.voice-prompt-panel {
  background: linear-gradient(180deg, #ffffff, #f7fbf9);
}

.panel-heading-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.85rem;
}

.panel-kicker {
  color: #0d5b4f;
  background: rgba(18, 113, 95, 0.08);
  padding: 0.38rem 0.68rem;
}

.suggested-prompts {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
}

.prompt-chip {
  padding: 0.62rem 0.9rem;
  border: 1px solid rgba(18, 113, 95, 0.14);
  border-radius: 16px;
  background: #ffffff;
  color: #17322c;
  font-size: 0.88rem;
  line-height: 1.28;
  text-align: left;
  box-shadow: 0 10px 22px rgba(17, 42, 36, 0.05);
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
}

.prompt-chip:hover:enabled {
  transform: translateY(-2px);
  border-color: rgba(18, 113, 95, 0.3);
  box-shadow: 0 16px 28px rgba(17, 42, 36, 0.09);
}

.prompt-chip:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.voice-insight-stack {
  display: grid;
  gap: 0.9rem;
  align-content: start;
}

.assistant-card {
  background: radial-gradient(circle at top right, rgba(42, 178, 123, 0.12), transparent 30%),
    linear-gradient(180deg, #f6fbf9, #ebf5f1);
  border: 1px solid rgba(18, 113, 95, 0.14);
  box-shadow: 0 18px 36px rgba(13, 77, 68, 0.08);
}

.assistant-message {
  white-space: pre-line;
  font-family: 'Georgia', 'Times New Roman', serif;
  font-size: 1.02rem;
  line-height: 1.76;
  letter-spacing: 0.01em;
  color: #14362f;
}

.typing-cursor {
  display: inline-block;
  margin-left: 0.08rem;
  font-weight: 600;
  animation: cursor-blink 1s steps(1) infinite;
}

.info-grid,
.response-grid {
  display: grid;
  gap: 0.85rem;
}

.info-card {
  display: flex;
  gap: 0.8rem;
  padding: 0.95rem 1rem;
  border: 1px solid transparent;
}

.info-card.warning {
  background: #fff8e7;
  border-color: #f2d58b;
}

.info-card.danger {
  background: #fff0ee;
  border-color: #f1b8b2;
}

.info-card h3 {
  margin: 0 0 0.35rem;
  font-size: 0.98rem;
  font-weight: 700;
}

.info-card p {
  margin: 0;
  line-height: 1.45;
  color: #4a615b;
}

.info-card-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.6);
  font-size: 1.1rem;
}

.response-grid-compact {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.glass-card {
  background: linear-gradient(180deg, #f7fbfa, #f1f7f5);
}

.processing-card {
  color: #fff;
  background: linear-gradient(135deg, #0f3d36, #155d4f);
}

.response-label {
  padding: 0.36rem 0.68rem;
  margin-bottom: 0.65rem;
  color: #0d5b4f;
  background: rgba(18, 113, 95, 0.1);
}

.processing-card .response-label {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.12);
}

.response-card p,
.audio-card p {
  margin: 0;
  line-height: 1.45;
}

.processing-row {
  display: flex;
  align-items: center;
  gap: 0.8rem;
}

.cancel-button,
.test-button {
  min-height: 40px;
  border-radius: 14px;
}

.test-button {
  border-color: rgba(18, 113, 95, 0.12);
  background: #fbfcfc;
  color: #17322c;
  font-weight: 600;
  white-space: nowrap;
}

.audio-card audio {
  border-radius: 12px;
}

.debug-card summary {
  cursor: pointer;
  font-weight: 700;
  color: #17322c;
}

.debug-log {
  max-height: 180px;
  margin-top: 0.65rem;
  padding: 0.8rem;
  overflow-y: auto;
  border-radius: 16px;
  background: #f6f9f8;
  border: 1px solid rgba(17, 42, 36, 0.08);
}

.debug-empty,
.debug-item {
  font-size: 0.88rem;
  line-height: 1.5;
  color: #4a615b;
}

.debug-item + .debug-item {
  margin-top: 0.45rem;
}

.sound-wave {
  gap: 4px;
  height: 24px;
  align-items: center;
}

.wave-bar {
  width: 4px;
  height: 10px;
  background-color: #c44558;
  border-radius: 2px;
  animation: wave 0.5s ease-in-out infinite;
}

.wave-bar:nth-child(1) {
  animation-delay: 0s;
}

.wave-bar:nth-child(2) {
  animation-delay: 0.1s;
}

.wave-bar:nth-child(3) {
  animation-delay: 0.2s;
}

.wave-bar:nth-child(4) {
  animation-delay: 0.3s;
}

.wave-bar:nth-child(5) {
  animation-delay: 0.4s;
}

.rolling-spinner {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  border: 2px solid rgba(255, 255, 255, 0.22);
  border-top: 2px solid #ffffff;
  border-radius: 50%;
  animation: roll 1s linear infinite;
}

.voice-modal-fade-enter-active,
.voice-modal-fade-leave-active {
  transition: opacity 0.22s ease;
}

.voice-modal-fade-enter,
.voice-modal-fade-leave-to {
  opacity: 0;
}

@keyframes cursor-blink {
  50% {
    opacity: 0;
  }
}

@keyframes wave {
  0%,
  100% {
    height: 10px;
  }
  50% {
    height: 25px;
  }
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(196, 69, 88, 0.35);
  }
  70% {
    box-shadow: 0 0 0 24px rgba(196, 69, 88, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(196, 69, 88, 0);
  }
}

@keyframes roll {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@media (max-width: 991px) {
  .voice-modal-overlay {
    padding: 16px;
  }

  .voice-assistant-shell {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .voice-modal-header {
    flex-direction: column;
    align-items: stretch;
  }

  .voice-modal-header-actions {
    justify-content: space-between;
  }

  .panel-heading-row {
    flex-direction: column;
    align-items: stretch;
  }

  .test-button {
    width: 100%;
  }

  .voice-stage {
    min-height: 160px;
  }
}

@media (max-width: 575px) {
  .voice-modal-overlay {
    padding: 10px;
  }

  .voice-modal-window {
    border-radius: 24px;
  }

  .voice-modal-header,
  .voice-assistant-shell {
    padding: 0.9rem;
  }

  .voice-mini-stats {
    grid-template-columns: 1fr;
  }
}
</style>
